import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';
import {
  Autocomplete,
  CircularProgress,
  TextField,
  debounce,
  Typography,
  colors,
} from '@mui/material';
import { Search } from '@mui/icons-material';

import { IGetCollegeSearch } from 'apis/type';
import {
  isSignupAllCountries,
  isSignupCountryChosen,
  SIGNUP_ALL_COLLEGE_SEARCH_SIZE,
} from 'constants/signup';

interface Props {
  listKey: string;
  valueKey: string;
  collegeCountryId?: number;
  variant?: 'outlined' | 'standard';
  size?: 'small' | 'medium';
  noOptionsText?: string;
  helperText?: string;
  placeholder?: string;
  hideSearchAdornment?: boolean;
  value?: any;
  onChange: (value: any) => void;
  getOptionLabel: (option: any) => string;
  renderOption?: (props: any, option: any) => React.ReactNode;
  getApi: (request: IGetCollegeSearch) => Promise<AxiosResponse<any>>;
}

const MuiAutocomplete = (props: Props) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  const {
    listKey,
    valueKey,
    variant = 'outlined',
    size = 'medium',
    noOptionsText,
    helperText,
    placeholder,
    hideSearchAdornment = false,
    value,
    onChange,
    renderOption,
    getOptionLabel,
    getApi,
    collegeCountryId,
  } = props;

  // value prop이 명시된 경우에만 controlled 모드로 동작 (회원가입 화면 등 기존 동작 보존)
  const controlledProps = 'value' in props ? { value: value ?? null } : {};

  const onChangeOptions = useCallback(
    (college_name: string) => {
      if (!isSignupCountryChosen(collegeCountryId)) {
        setOptions([]);
        setTotalCount(0);
        setIsLoading(false);
        return;
      }

      const keyword = college_name.trim();
      const isAllCountries = isSignupAllCountries(collegeCountryId);
      const request: IGetCollegeSearch = {
        page: 1,
        college_name: keyword,
        searchType: '1',
      };
      if (isAllCountries) {
        request.size = SIGNUP_ALL_COLLEGE_SEARCH_SIZE;
      } else {
        request.college_country_id = Number(collegeCountryId);
      }

      getApi(request)
        .then(({ data }) => {
          setTotalCount(data?.count);
          setOptions(data[listKey] ?? []);
        })
        .finally(() => setIsLoading(false));
    },
    [collegeCountryId, getApi, listKey]
  );

  useEffect(() => {
    setOptions([]);
    setTotalCount(0);
    onChangeOptions('');
  }, [collegeCountryId, onChangeOptions]);

  const isOptionEqualToValue = (option: any, value: any) =>
    option[valueKey] === value[valueKey];

  const onChangeTextField = debounce(e => {
    const keyword = e.target.value;
    onChangeOptions(keyword);
  }, 500);

  const TextFieldEndAdornment = ({
    endAdornment,
  }: {
    endAdornment: React.ReactNode;
  }) => (
    <Fragment>
      {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
      {endAdornment}
    </Fragment>
  );

  const renderStartAdornment = (): React.ReactNode => {
    if (totalCount) {
      return (
        <Typography
          variant="caption"
          color={colors.grey[500]}
          sx={{ mr: 1 }}
        >
          (총 {totalCount}명)
        </Typography>
      );
    }
    if (hideSearchAdornment) return null;
    return <Search color="disabled" sx={{ fontSize: 16, mr: 1 }} />;
  };

  return (
    <Autocomplete
      fullWidth
      size={size}
      {...controlledProps}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      isOptionEqualToValue={isOptionEqualToValue}
      renderOption={renderOption}
      getOptionLabel={getOptionLabel}
      noOptionsText={<Typography variant="caption">{noOptionsText}</Typography>}
      options={options}
      loading={isLoading}
      onChangeCapture={() => setIsLoading(true)}
      onChange={(_, value) => onChange(value)}
      renderInput={params => (
        <TextField
          {...params}
          variant={variant}
          size={size}
          helperText={helperText}
          placeholder={placeholder}
          onChange={onChangeTextField}
          InputProps={{
            ...params.InputProps,
            startAdornment: renderStartAdornment(),
            endAdornment: (
              <TextFieldEndAdornment
                endAdornment={params.InputProps.endAdornment}
              />
            ),
            sx: { fontSize: 14 },
          }}
        />
      )}
    />
  );
};

export default MuiAutocomplete;
